import { useCallback, useEffect, useState } from "react";
import { Loader2, Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { opsApi } from "@/operations/lib/api";

const NUMBERS = [
  ["default_lead_days", "Default PM lead time (business days)"],
  ["override_threshold_pct", "Material override threshold (% of suggested)"],
  ["override_threshold_amount", "Material override threshold ($)"],
  ["retention_followup_start_days", "Retention follow-up starts (days)"],
  ["retention_followup_cadence_days", "Retention follow-up cadence (days)"],
  ["generate_months_past_completion", "Generate periods past completion (months)"],
];

/** Settings › Billing — the calendar rules and the requirement library (FIN-1). */
export default function BillingSettingsPage() {
  const [d, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [holiday, setHoliday] = useState("");
  const [newType, setNewType] = useState({ key: "", label: "", cadence: "every_month" });

  const load = useCallback(async () => {
    try { setData(await opsApi.billingSettings()); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not load the billing settings"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!d) {
    return <div className="flex items-center gap-2 py-16 text-sm text-slate-500">
      <Loader2 className="h-4 w-4 animate-spin" /> Loading billing settings…
    </div>;
  }
  const s = d.settings;

  const save = async (patch) => {
    setBusy(true);
    try { setData(await opsApi.saveBillingSettings(patch)); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save that"); }
    finally { setBusy(false); }
  };

  const saveType = async (row) => {
    setBusy(true);
    try {
      const res = await opsApi.saveRequirementType(row);
      setData({ ...d, requirement_types: res.requirement_types });
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save that requirement"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-5" data-testid="settings-billing">
      <h2 className="text-lg font-semibold text-slate-900">Billing</h2>

      <section className="rounded-xl border border-slate-200 bg-white p-5">
        <h3 className="text-sm font-semibold text-slate-900">Deadlines</h3>
        <div className="mt-3 grid gap-3 md:grid-cols-2">
          <div>
            <span className="text-xs font-medium text-slate-500">
              PM lead time by GC submission day (business days)
            </span>
            <div className="mt-1 flex flex-wrap gap-2">
              {Object.entries(s.lead_times).map(([day, days]) => (
                <span key={day} className="flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-xs">
                  {day}th →
                  <Input type="number" defaultValue={days} disabled={!d.can_edit}
                         data-testid={`billing-lead-${day}`}
                         className="h-6 w-14 text-xs"
                         onBlur={(e) => save({
                           lead_times: { ...s.lead_times, [day]: Number(e.target.value) } })} />
                </span>
              ))}
            </div>
          </div>
          {NUMBERS.map(([key, label]) => (
            <label key={key} className="block">
              <span className="text-xs font-medium text-slate-500">{label}</span>
              <Input type="number" step="any" defaultValue={s[key]} disabled={!d.can_edit}
                     className="mt-1 h-8 text-sm" data-testid={`billing-${key}`}
                     onBlur={(e) => Number(e.target.value) !== s[key]
                       && save({ [key]: Number(e.target.value) })} />
            </label>
          ))}
        </div>
      </section>

      <section className="rounded-xl border border-slate-200 bg-white p-5">
        <h3 className="text-sm font-semibold text-slate-900">Holidays</h3>
        <p className="mt-1 text-xs text-slate-500">
          A GC date landing on a weekend or one of these rolls back to the previous business day.
        </p>
        <div className="mt-3 flex flex-wrap gap-1.5" data-testid="billing-holidays">
          {s.holidays.map((h) => (
            <span key={h} className="flex items-center gap-1 rounded-full border border-slate-200 px-2 py-0.5 text-xs">
              {h}
              {d.can_edit && (
                <button onClick={() => save({ holidays: s.holidays.filter((x) => x !== h) })}
                        data-testid={`billing-holiday-remove-${h}`}
                        className="text-slate-400 hover:text-rose-600">
                  <X className="h-3 w-3" />
                </button>
              )}
            </span>
          ))}
        </div>
        {d.can_edit && (
          <div className="mt-3 flex gap-2">
            <Input type="date" value={holiday} onChange={(e) => setHoliday(e.target.value)}
                   className="h-8 w-44 text-sm" data-testid="billing-holiday-input" />
            <Button size="sm" variant="outline" disabled={!holiday || busy}
                    data-testid="billing-holiday-add"
                    onClick={() => {
                      save({ holidays: [...new Set([...s.holidays, holiday])].sort() });
                      setHoliday("");
                    }}>
              <Plus className="mr-1.5 h-3.5 w-3.5" /> Add
            </Button>
          </div>
        )}
      </section>

      <section className="rounded-xl border border-slate-200 bg-white p-5">
        <h3 className="text-sm font-semibold text-slate-900">Requirement library</h3>
        <table className="mt-3 w-full text-sm" data-testid="billing-requirement-types">
          <thead className="text-[11px] uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-2 py-1 text-left">Requirement</th>
              <th className="px-2 py-1 text-left">Default cadence</th>
              <th className="px-2 py-1 text-left">Signature</th>
              <th className="px-2 py-1 text-left">Notary</th>
              <th className="px-2 py-1 text-left">Active</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {d.requirement_types.map((t) => (
              <tr key={t.key} data-testid={`billing-type-${t.key}`}>
                <td className="px-2 py-1.5 font-medium text-slate-800">{t.label}</td>
                <td className="px-2 py-1.5">
                  <select value={t.cadence} disabled={!d.can_edit}
                          data-testid={`billing-type-cadence-${t.key}`}
                          onChange={(e) => saveType({ ...t, cadence: e.target.value })}
                          className="rounded-md border border-slate-200 px-1.5 py-0.5 text-xs">
                    {d.cadences.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                  </select>
                </td>
                {[["requires_signature", "sig"], ["requires_notarization", "notary"],
                  ["active", "active"]].map(([field, tag]) => (
                  <td key={field} className="px-2 py-1.5">
                    <Checkbox checked={!!t[field]} disabled={!d.can_edit}
                              data-testid={`billing-type-${tag}-${t.key}`}
                              onCheckedChange={(v) => saveType({ ...t, [field]: !!v })} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {d.can_edit && (
          <div className="mt-3 flex flex-wrap gap-2">
            <Input placeholder="new_requirement_key" value={newType.key}
                   onChange={(e) => setNewType({ ...newType, key: e.target.value.trim() })}
                   className="h-8 w-52 text-sm" data-testid="billing-type-new-key" />
            <Input placeholder="Label" value={newType.label}
                   onChange={(e) => setNewType({ ...newType, label: e.target.value })}
                   className="h-8 w-52 text-sm" data-testid="billing-type-new-label" />
            <Button size="sm" variant="outline" disabled={!newType.key || busy}
                    data-testid="billing-type-new-save"
                    onClick={() => { saveType(newType); setNewType({ key: "", label: "", cadence: "every_month" }); }}>
              <Plus className="mr-1.5 h-3.5 w-3.5" /> Add requirement
            </Button>
          </div>
        )}
      </section>
    </div>
  );
}
